/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { computed } from 'mobx';

import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';

import { IDlvryMediaGalleryData } from './dlvry-media-gallery.data';
import { IDlvryMediaGalleryProps } from './dlvry-media-gallery.props.autogenerated';
import { MediaLocation } from '@msdyn365-commerce/retail-proxy';
import ReactDOM from 'react-dom';
import ZoomedImage from './components/zoomed-image';
import SwipeArea from './components/swipe-area';
import PlaceholderImage from './components/placeholder-image';

export interface IDlvryMediaGalleryViewProps extends IDlvryMediaGalleryProps<IDlvryMediaGalleryData> {
    componentRef: React.RefObject<HTMLDivElement>;
    baseClassName: string;
    ModuleProps: IModuleProps;
    MediaGalleryContainerProps: INodeProps;
    currentImage: JSX.Element | null;
    slideshow: JSX.Element | null;
    zoomedInImage: JSX.Element | null;
}

type UniqueImageUri = `${number}:${string}`;

interface IDlvryMediaGalleryState {
    images: MediaLocation[];
    currentImageIndex: number;
    zoomedImage?: MediaLocation | null;
    scrollOffset: number;
    loadedImages: Set<UniqueImageUri>;
    failedImages: Set<UniqueImageUri>;
    slideshowColumns: number;
}

/**
 *
 * DlvryMediaGallery component
 * @extends {React.PureComponent<IDlvryMediaGalleryProps<IDlvryMediaGalleryData>>}
 */
@observer
class DlvryMediaGallery extends React.PureComponent<IDlvryMediaGalleryProps<IDlvryMediaGalleryData>, IDlvryMediaGalleryState> {
    private baseClassName: string = 'dlvry-media-gallery';

    private componentRef: React.RefObject<HTMLDivElement> = React.createRef();
    private zoomedInWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();

    private imageParameters: string = 'w=550&h=550&f=webp&m=1';
    private thumbnailImageParameters: string = 'w=116&h=116&f=webp&m=1';

    @computed get imagesToLoad(): number {
        return this.state.images.length;
    }

    @computed get validImages(): MediaLocation[] {
        const useOmniChannelMedia = this.props.context.app.config?.OmniChannelMedia as boolean | undefined;

        if (useOmniChannelMedia) {
            // For now I don't think we need to validate the images when Omni Channel Media is enabled since the API should only return valid images, but we can always add this later if needed.
            return this.state.images;
        }

        const { images, loadedImages, failedImages } = this.state;
        return images.filter(({ Uri }, i) => {
            if (!Uri) {
                return false;
            }
            const key = `${i}:${Uri}` as UniqueImageUri;
            return loadedImages.has(key) && !failedImages.has(key);
        });
    }

    @computed get maxOffset(): number {
        return Math.max(this.validImages.length - this.state.slideshowColumns, 0);
    }

    constructor(props: IDlvryMediaGalleryProps<IDlvryMediaGalleryData>) {
        super(props);
        this.state = {
            images: props.data.mediaLocationsForSelectedVariant.result || [],
            currentImageIndex: 0,
            zoomedImage: null,
            scrollOffset: 0,
            loadedImages: new Set<UniqueImageUri>(),
            failedImages: new Set<UniqueImageUri>(),
            slideshowColumns: 5
        };
        this.onImageClick = this.onImageClick.bind(this);
        this.closeZoomedInImage = this.closeZoomedInImage.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    private handleCalculateSlideshowColumns = (): void => {
        const slideshowColumns = window.innerWidth > 992 ? 5
            : window.innerWidth > 520 ? 4
                : 3;
        if (slideshowColumns !== this.state.slideshowColumns) {
            this.setState({
                slideshowColumns,
                scrollOffset: Math.min(this.state.scrollOffset, Math.max(this.validImages.length - slideshowColumns, 0))
            });
        }
    };

    public async componentDidMount(): Promise<void> {
        window.addEventListener('resize', this.handleCalculateSlideshowColumns);
        this.handleCalculateSlideshowColumns();

        // Add right and left hotkey
        document.addEventListener('keydown', this.handleKeyDown);

        const images = await this.props.data.mediaLocationsForSelectedVariant;

        this.setState({ images });

        // Set fallback images for any that failed to load before component mounted
        if (this.componentRef.current) {
            const images = this.componentRef.current.getElementsByTagName('img');
            const loadedImages = new Set(this.state.loadedImages);
            const failedImages = new Set(this.state.failedImages);

            for (let i = 0; i < images.length; i++) {
                const img = images[i];
                if (img.complete) {
                    loadedImages.add(img.dataset.imageUri as UniqueImageUri || '');
                    if (img.naturalWidth === 0) {
                        failedImages.add(img.dataset.imageUri as UniqueImageUri || '');
                    }
                }
            }

            this.setState((prevState) => {
                return {
                    ...prevState,
                    loadedImages,
                    failedImages
                };
            });
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleCalculateSlideshowColumns);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    private handleKeyDown = (event: KeyboardEvent): void => {
        const { currentImageIndex, zoomedImage } = this.state;

        // Only active if zoomed in
        if (!zoomedImage) {
            return;
        }

        if (event.key === 'ArrowRight') {
            if (currentImageIndex < this.validImages.length - 1) {
                this.setState({
                    currentImageIndex: currentImageIndex + 1,
                    zoomedImage: this.validImages[currentImageIndex + 1]
                });
            }
            event.preventDefault();
        } else if (event.key === 'ArrowLeft') {
            if (currentImageIndex > 0) {
                this.setState({
                    currentImageIndex: currentImageIndex - 1,
                    zoomedImage: this.validImages[currentImageIndex - 1]
                });
            }
            event.preventDefault();
        } else if (event.key === 'Escape') {
            this.closeZoomedInImage();
            event.preventDefault();
        }
    };

    public render(): JSX.Element | null {
        const viewProps: IDlvryMediaGalleryViewProps = {
            ...this.props,
            componentRef: this.componentRef,
            ModuleProps: {
                moduleProps: this.props,
                className: this.baseClassName
            },
            MediaGalleryContainerProps: {
                className: `${this.baseClassName}__container`
            },
            baseClassName: this.baseClassName,
            currentImage: this.renderCurrentImage(),
            slideshow: this.renderSlideshow(),
            zoomedInImage: this.renderZoomedInImage()
        };
        return this.props.renderView(viewProps);
    }

    private addImageParameters(baseUri: string, params: string): string {
        if (baseUri.indexOf('?') === -1) {
            return `${baseUri}?${params}`;
        }
        return `${baseUri}&${params}`;
    }

    private renderCurrentImage(): JSX.Element | null {
        const currentImage = this.validImages[this.state.currentImageIndex];

        const moveIndex = (offset: number): void => {
            const { currentImageIndex } = this.state;
            const newIndex = currentImageIndex + offset;
            if (newIndex < 0) {
                this.setState({ currentImageIndex: this.validImages.length - 1 });
            } else if (newIndex >= this.validImages.length) {
                this.setState({ currentImageIndex: 0 });
            } else {
                this.setState({ currentImageIndex: newIndex });
            }
        };

        const allImagesHasLoaded = this.state.loadedImages.size >= this.imagesToLoad;

        return (
            <div className={`${this.baseClassName}__current-image__container`}>
                <div className={`${this.baseClassName}__current-image`}>
                    {currentImage && (
                        <SwipeArea
                            onSwipeLeft={() => moveIndex(1)}
                            onSwipeRight={() => moveIndex(-1)}
                        >
                            <img
                                src={this.addImageParameters(currentImage.Uri || '', this.imageParameters)}
                                title={currentImage.AltText}
                                onClick={() => this.onImageClick(this.state.currentImageIndex, currentImage)}
                            />
                        </SwipeArea>
                    )}
                    {!currentImage && (
                        <PlaceholderImage
                            context={this.props.context}
                            baseClassName={this.baseClassName}
                            hide={!allImagesHasLoaded}
                        />
                    )}
                    {this.validImages.length > 1 && (
                        <>
                            <button
                                className={`${this.baseClassName}__current-image__prev`}
                                aria-label='Previous Image'
                                disabled={!allImagesHasLoaded}
                                onClick={() => moveIndex(-1)}
                            />
                            <button
                                className={`${this.baseClassName}__current-image__next`}
                                aria-label='Next Image'
                                disabled={!allImagesHasLoaded}
                                onClick={() => moveIndex(1)}
                            />
                        </>
                    )}
                    {allImagesHasLoaded && this.validImages.length > 1 && (
                        <div className={`${this.baseClassName}__current-image__position-indicators`}>
                            {this.validImages.map((_, index) => (
                                <div
                                    key={index}
                                    className={`${this.baseClassName}__current-image__position-indicator ${index === this.state.currentImageIndex ? 'active' : ''}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderSlideshow(): JSX.Element | null {
        const { images } = this.state;
        const useOmniChannelMedia = this.props.context.app.config?.OmniChannelMedia as boolean | undefined;
        const fallbackImageSrc = this.getFallbackImageSrc();

        // First sort images by "IsDefault", which will always be the first image, then by "DisplayOrder"
        images.sort((a, b) => {
            if (a.IsDefault && !b.IsDefault) {
                return -1;
            }
            if (!a.IsDefault && b.IsDefault) {
                return 1;
            }
            return (a.DisplayOrder || 0) - (b.DisplayOrder || 0);
        });

        return (
            <div className={`${this.baseClassName}__slideshow`} style={{ '--scroll-offset': Math.min(this.state.scrollOffset, this.maxOffset) * -1 } as React.CSSProperties}>
                {images.map((image, index) => {
                    const validImageIndex = this.validImages.indexOf(image);
                    const imageUri = image.Uri
                        ? this.addImageParameters(image.Uri, this.thumbnailImageParameters)
                        : undefined;
                    const uniqueImageUri = `${index}:${image.Uri}` as UniqueImageUri;
                    const isLoaded = image.Uri ? this.state.loadedImages.has(uniqueImageUri) : false;
                    const isFailed = image.Uri ? this.state.failedImages.has(uniqueImageUri) : false;
                    const imgSrc = imageUri && !isFailed ? imageUri : fallbackImageSrc;

                    // If Omni Channel Media is enabled only valid images should be returned by the API.
                    // Otherwise we always get the configured amount and need to check if they exist before showing them (has Loaded and not Failed).
                    // And lastly we only show the slideshow if there are more than 1 valid images.
                    const shouldShowImage = useOmniChannelMedia ||
                        (image.Uri && isLoaded && !isFailed && (index > 1 || this.validImages.length > 1));

                    // Always render at least 1 image to prevent layout shift, but hide it if there are no valid images
                    const style: React.CSSProperties | undefined = shouldShowImage ? undefined
                        : this.validImages.length <= 1 ? { visibility: 'hidden' }
                        : { display: 'none' };

                    return (
                        <div
                            key={uniqueImageUri}
                            className={classnames(`${this.baseClassName}__slideshow__image-container`, {
                                // Valid if loading has been attempted and it succeeded, or if Omni Channel Media is enabled since we assume all returned images are
                                valid: useOmniChannelMedia ? !!image.Uri : image.Uri && isLoaded && !isFailed
                            })}
                            style={style}
                        >
                            <img
                                className={classnames({ current: validImageIndex === this.state.currentImageIndex })}
                                src={imgSrc}
                                title={image.AltText}
                                data-image-uri={uniqueImageUri}
                                data-fallback={imgSrc === fallbackImageSrc ? 'true' : 'false'}
                                onLoad={() => {
                                    if (image.Uri) {
                                        this.setState((prevState) => {
                                            const loadedImages = new Set(prevState.loadedImages);
                                            loadedImages.add(uniqueImageUri);
                                            return {
                                                ...prevState,
                                                loadedImages
                                            };
                                        });
                                    }
                                }}
                                onError={() => {
                                    if (image.Uri) {
                                        this.setState((prevState) => {
                                            const loadedImages = new Set(prevState.loadedImages);
                                            const failedImages = new Set(prevState.failedImages);
                                            loadedImages.add(uniqueImageUri);
                                            failedImages.add(uniqueImageUri);
                                            return { ...prevState, loadedImages, failedImages };
                                        });
                                    }
                                }}
                                onClick={() => this.onThumbnailClick(validImageIndex)}
                            />
                        </div>
                    );
                })}
                <div className={`${this.baseClassName}__slideshow__controls`}>
                    <button
                        disabled={this.state.scrollOffset === 0}
                        onClick={() => {
                            this.setState({ scrollOffset: Math.max(this.state.scrollOffset - this.state.slideshowColumns, 0) });
                        }}
                    >
                        {this.props.resources.previousButtonText}
                    </button>
                    <button
                        disabled={this.state.scrollOffset >= this.maxOffset}
                        onClick={() => {
                            this.setState({ scrollOffset: Math.min(this.state.scrollOffset + this.state.slideshowColumns, this.maxOffset) });
                        }}
                    >
                        {this.props.resources.nextButtonText}
                    </button>
                </div>
            </div>
        );
    }

    private renderZoomedInImage(): JSX.Element | null {
        if (!this.state.zoomedImage || typeof window === 'undefined') {
            return null;
        }

        const { resources } = this.props;
        const { currentImageIndex, zoomedImage } = this.state;

        const element = (
            <div className={`${this.baseClassName}__zoomed-in-image__wrapper`} ref={this.zoomedInWrapperRef}>
                <div className={`${this.baseClassName}__zoomed-in-image__topbar`}>
                    <div>
                        {(currentImageIndex || 0) + 1} / {this.validImages.length}
                    </div>
                    <div className={`${this.baseClassName}__zoomed-in-image__actions`}>
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__actions__fullscreen`}
                            title={resources.mediaGalleryFullscreenButtonTitle}
                            onClick={() => {
                                if (this.zoomedInWrapperRef.current) {
                                    if (document.fullscreenElement) {
                                        void document.exitFullscreen();
                                    } else {
                                        void this.zoomedInWrapperRef.current.requestFullscreen();
                                    }
                                }
                            }}
                        />
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__actions__close`}
                            title={resources.mediaGalleryCloseButtonTitle}
                            onClick={() => {
                                this.closeZoomedInImage();
                            }}
                        />
                    </div>
                </div>
                <ZoomedImage
                    baseClassName={this.baseClassName}
                    closeZoomedInImage={this.closeZoomedInImage}
                    zoomedImage={zoomedImage}
                    goToPreviousButton={
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__previous`}
                            disabled={this.validImages.length <= 1}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (currentImageIndex > 0) {
                                    this.setState({
                                        currentImageIndex: currentImageIndex - 1,
                                        zoomedImage: this.validImages[currentImageIndex - 1]
                                    });
                                } else if (currentImageIndex === 0) {
                                    this.setState({
                                        currentImageIndex: this.validImages.length - 1,
                                        zoomedImage: this.validImages[this.validImages.length - 1]
                                    });
                                }
                            }}
                            title={resources.mediaGalleryPreviousButtonTitle}
                        />
                    }
                    goToNextButton={
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__next`}
                            disabled={this.validImages.length <= 1}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (currentImageIndex < this.validImages.length - 1) {
                                    this.setState({
                                        currentImageIndex: currentImageIndex + 1,
                                        zoomedImage: this.validImages[currentImageIndex + 1]
                                    });
                                } else if (currentImageIndex === this.validImages.length - 1) {
                                    this.setState({
                                        currentImageIndex: 0,
                                        zoomedImage: this.validImages[0]
                                    });
                                }
                            }}
                            title={resources.mediaGalleryNextButtonTitle}
                        />
                    }
                />
            </div>
        );

        return ReactDOM.createPortal(element, document.body);
    }

    private onImageClick(index: number, image: MediaLocation): void {
        this.setState({ currentImageIndex: index, zoomedImage: image });
        document.body.classList.add('overflow-hidden');
    }

    private onThumbnailClick(index: number): void {
        this.setState({ currentImageIndex: index });
    }

    private closeZoomedInImage = (): void => {
        this.setState({ zoomedImage: null });
        document.body.classList.remove('overflow-hidden');
    };

    /**
     * Converts a given string representing a file path with an extension to multiple formats.
     * @param inputString The input string representing the file path with extension.
     * @returns A string containing comma-separated paths for each supported format.
     */
    private convertToMultipleExtensions = (inputString: string): string => {
        const formats = ['jpg', 'png', 'webp'];

        const baseParts = inputString.split('.', 1);
        if (baseParts.length > 0) {
            const [basePath] = baseParts;

            return formats.map(ext => `${basePath}.${ext}`).join(',');
        }
        return inputString;
    };

    private getFallbackImageSrc = (): string | undefined => {
        const placeholderImageName = this.props.context.request.app.config?.placeholderImageName as string | undefined;
        if (placeholderImageName) {
            const baseImageUrl = this.props.context.request.apiSettings.baseImageUrl;
            return `${baseImageUrl}&fallback=${this.convertToMultipleExtensions(placeholderImageName)}`;
        }
        return;
    };
}

export default DlvryMediaGallery;
